import {
	Autocomplete,
	Backdrop,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { alpha } from "@mui/material/styles";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import DatePickerComponent from "../../../components/DatePickerComponent";
import TableComponent from "../../../components/TableComponent";
import {
	generateDeviceReport,
	generateEventsReport,
	generateFacesReport,
	generateGeneralEventsReport,
	generateRecognitionExitEventsReport,
	generateResponsibleEventsReport,
	generateResponsibleExitEventsReport,
	generateStudentEntriesLast30DaysReport,
	generateStudentEvents,
	generateStudentEventsByStudentReport,
	generateStudentEventsReport,
	generateStudentExitEventsReport,
	generateStudentsByClassReport,
	generateStudentsWithoutEventsReport,
} from "../../../services/ReportService";
import type { IReportProps } from "../../../services/ReportService/types";
import { getAllSchoolClassByLicenseAsync } from "../../../services/SchoolClass";
import { getAllStudentByLicenseAsync } from "../../../services/StudentService";
import type { IStudentSimpleProps } from "../../../services/StudentService/types";
import type { IPaginationParams } from "../../../services/types";

type SchoolClassOption = {
	id: number;
	name: string;
};

type ReportRow = {
	id: number;
	reportName: string;
	prefix: string;
	group: (
		| "Alunos"
		| "Eventos"
		| "Funcionários"
		| "Responsáveis"
		| "Reconhecimento"
		| "Outros"
	)[];
	supportsIncludeImages: boolean;
	requiresStudent?: boolean;
	requiresClasses?: boolean;
	requiresPeriod?: boolean;
	usesLast30DaysPeriod?: boolean;
	run: (params: {
		report: IReportProps;
		studentId?: number;
		classIds?: number[];
	}) => Promise<Blob>;
};

type ReportRowView = ReportRow & { sortOrder: number };

type GroupTone =
	| "primary"
	| "warning"
	| "success"
	| "info"
	| "secondary"
	| "neutral";

const ReportTable = () => {
	const reports = useMemo<ReportRow[]>(
		() => [
			// Alunos
			{
				id: 1,
				reportName: "Alunos - Cadastro",
				prefix: "alunos_cadastro",
				group: ["Alunos"],
				supportsIncludeImages: true,
				run: ({ report }) => generateStudentEventsReport(report),
			},
			{
				id: 2,
				reportName: "Alunos - Eventos",
				prefix: "alunos_eventos",
				group: ["Alunos", "Eventos"],
				supportsIncludeImages: true,
				run: ({ report }) => generateStudentEvents(report),
			},
			{
				id: 3,
				reportName: "Alunos - Saídas",
				prefix: "alunos_saida",
				group: ["Alunos", "Eventos"],
				supportsIncludeImages: true,
				run: ({ report }) => generateStudentExitEventsReport(report),
			},
			{
				id: 4,
				reportName: "Alunos - Sem Eventos",
				prefix: "alunos_sem_eventos",
				group: ["Alunos", "Eventos"],
				supportsIncludeImages: true,
				run: ({ report }) => generateStudentsWithoutEventsReport(report),
			},
			{
				id: 12,
				reportName: "Alunos - Eventos por Aluno",
				prefix: "alunos_por_aluno",
				group: ["Alunos", "Eventos"],
				supportsIncludeImages: true,
				requiresStudent: true,
				run: ({ report, studentId }) =>
					generateStudentEventsByStudentReport(studentId ?? 0, report),
			},
			{
				id: 13,
				reportName: "Alunos - Últimos 30 Dias",
				prefix: "alunos_ultimos_30",
				group: ["Alunos", "Eventos"],
				supportsIncludeImages: true,
				requiresStudent: true,
				usesLast30DaysPeriod: true,
				run: ({ report, studentId }) =>
					generateStudentEntriesLast30DaysReport(
						studentId ?? 0,
						report.ExportType,
						report.IncludeImages,
					),
			},
			{
				id: 14,
				reportName: "Alunos - Turmas",
				prefix: "alunos_turmas",
				group: ["Alunos"],
				supportsIncludeImages: true,
				requiresClasses: true,
				requiresPeriod: false,
				run: ({ report, classIds }) =>
					generateStudentsByClassReport(
						classIds ?? [],
						report.ExportType,
						report.IncludeImages,
					),
			},

			// Eventos gerais
			{
				id: 5,
				reportName: "Eventos - Geral",
				prefix: "eventos_geral",
				group: ["Eventos"],
				supportsIncludeImages: true,
				run: ({ report }) => generateEventsReport(report),
			},
			{
				id: 6,
				reportName: "Funcionários - Eventos",
				prefix: "funcionarios_eventos",
				group: ["Funcionários", "Eventos"],
				supportsIncludeImages: true,
				run: ({ report }) => generateGeneralEventsReport(report),
			},

			// Responsáveis
			{
				id: 7,
				reportName: "Responsáveis - Eventos",
				prefix: "responsaveis_eventos",
				group: ["Responsáveis", "Eventos"],
				supportsIncludeImages: true,
				run: ({ report }) => generateResponsibleEventsReport(report),
			},
			{
				id: 8,
				reportName: "Responsáveis - Saídas",
				prefix: "responsaveis_saida",
				group: ["Responsáveis", "Eventos"],
				supportsIncludeImages: true,
				run: ({ report }) => generateResponsibleExitEventsReport(report),
			},

			// Reconhecimento
			{
				id: 9,
				reportName: "Reconhecimento - Saídas",
				prefix: "reconhecimento_saida",
				group: ["Reconhecimento", "Eventos"],
				supportsIncludeImages: true,
				run: ({ report }) => generateRecognitionExitEventsReport(report),
			},

			// Outros
			{
				id: 10,
				reportName: "Dispositivos",
				prefix: "outros_dispositivos",
				group: ["Outros"],
				supportsIncludeImages: false,
				run: ({ report }) => generateDeviceReport(report),
			},
			{
				id: 11,
				reportName: "Rostos - Geral",
				prefix: "outros_rostos",
				group: ["Outros"],
				supportsIncludeImages: true,
				run: ({ report }) => generateFacesReport(report),
			},
		],
		[],
	);

	const groupColorMap: Record<ReportRow["group"][number], GroupTone> = {
		Alunos: "primary",
		Eventos: "warning",
		Funcionários: "success",
		Responsáveis: "info",
		Reconhecimento: "secondary",
		Outros: "neutral",
	};

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [selectedReport, setSelectedReport] = useState<number | null>(null);
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [type, setType] = useState("pdf");
	const [includeImages, setIncludeImages] = useState(true);
	const [studentQuery, setStudentQuery] = useState("");
	const [selectedStudent, setSelectedStudent] =
		useState<IStudentSimpleProps | null>(null);
	const [studentOptions, setStudentOptions] = useState<IStudentSimpleProps[]>(
		[],
	);
	const [classOptions, setClassOptions] = useState<SchoolClassOption[]>([]);
	const [selectedClasses, setSelectedClasses] = useState<SchoolClassOption[]>(
		[],
	);
	const [isClassLoading, setIsClassLoading] = useState(false);
	const [isStudentLoading, setIsStudentLoading] = useState(false);
	const [pagination, setPagination] = useState<IPaginationParams>({
		pageNumber: 1,
		pageSize: 10,
	});

	const selectedReportMeta = useMemo(
		() =>
			selectedReport ? reports.find((r) => r.id === selectedReport) : undefined,
		[selectedReport, reports],
	);

	const [loading, setLoading] = useState(false);

	const formatCpf = (cpf?: string) => {
		const digits = (cpf ?? "").replace(/\D/g, "");
		if (digits.length !== 11) return cpf ?? "";
		return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
	};

	const getStudentIdentifier = (student: IStudentSimpleProps) => {
		const registration = student.registration?.trim();
		const cpf = student.cpf?.trim();
		const formattedCpf = cpf ? formatCpf(cpf) : "";

		if (registration && formattedCpf) {
			return `Matrícula ${registration} • CPF ${formattedCpf}`;
		}

		if (registration) {
			return `Matrícula ${registration}`;
		}

		if (formattedCpf) {
			return `CPF ${formattedCpf}`;
		}

		return "Matrícula/CPF não informados";
	};

	const requiresStudent = selectedReportMeta?.requiresStudent ?? false;
	const requiresClasses = selectedReportMeta?.requiresClasses ?? false;
	const requiresPeriod = selectedReportMeta?.requiresPeriod ?? true;
	const usesLast30DaysPeriod =
		selectedReportMeta?.usesLast30DaysPeriod ?? false;

	useEffect(() => {
		if (!isDialogOpen || !requiresStudent) {
			setStudentOptions([]);
			setIsStudentLoading(false);
			return;
		}

		let isCancelled = false;
		const timer = setTimeout(async () => {
			setIsStudentLoading(true);
			try {
				const normalizedQuery = studentQuery.trim().toLowerCase();

				const students = await getAllStudentByLicenseAsync(
					{ pageNumber: 1, pageSize: 25 },
					{ search: studentQuery.trim() },
				);

				let finalOptions = students;

				if (normalizedQuery && students.length === 0) {
					const fallbackStudents = await getAllStudentByLicenseAsync(
						{ pageNumber: 1, pageSize: 200 },
						{ search: "" },
					);

					finalOptions = fallbackStudents
						.filter((student) => {
							const name = student.name?.toLowerCase() ?? "";
							const registration = student.registration?.toLowerCase() ?? "";
							const cpf = student.cpf?.toLowerCase() ?? "";
							return (
								name.includes(normalizedQuery) ||
								registration.includes(normalizedQuery) ||
								cpf.includes(normalizedQuery)
							);
						})
						.slice(0, 25);
				}

				if (!isCancelled) {
					setStudentOptions(finalOptions);
				}
			} catch {
				if (!isCancelled) {
					setStudentOptions([]);
				}
			} finally {
				if (!isCancelled) {
					setIsStudentLoading(false);
				}
			}
		}, 350);

		return () => {
			isCancelled = true;
			clearTimeout(timer);
		};
	}, [isDialogOpen, requiresStudent, studentQuery]);

	useEffect(() => {
		if (!isDialogOpen || !requiresClasses) {
			setClassOptions([]);
			setIsClassLoading(false);
			return;
		}

		let isCancelled = false;

		const loadClasses = async () => {
			setIsClassLoading(true);
			try {
				const classes = await getAllSchoolClassByLicenseAsync();
				if (!isCancelled) {
					setClassOptions(
						(classes ?? [])
							.filter(
								(item): item is SchoolClassOption =>
									typeof item?.id === "number" && !!item?.name,
							)
							.sort((a, b) =>
								a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }),
							),
					);
				}
			} catch {
				if (!isCancelled) {
					setClassOptions([]);
				}
			} finally {
				if (!isCancelled) {
					setIsClassLoading(false);
				}
			}
		};

		loadClasses();

		return () => {
			isCancelled = true;
		};
	}, [isDialogOpen, requiresClasses]);

	const handleOpenDialog = (report: number) => {
		setSelectedReport(report);
		setType("pdf");
		setIncludeImages(true);
		setStudentQuery("");
		setSelectedStudent(null);
		setSelectedClasses([]);
		setIsDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setIsDialogOpen(false);
		setSelectedReport(null);
		setStartDate("");
		setEndDate("");
		setType("pdf");
		setIncludeImages(true);
		setStudentQuery("");
		setSelectedStudent(null);
		setStudentOptions([]);
		setSelectedClasses([]);
		setClassOptions([]);
	};

	const fetchReportData = async (
		reportMeta: ReportRow,
		startDate: string,
		endDate: string,
		ExportType: string,
		includeImagesValue: boolean,
	) => {
		setLoading(true);
		try {
			const report: IReportProps = {
				StarTime: startDate,
				EndTime: endDate,
				ExportType: ExportType,
				IncludeImages: reportMeta.supportsIncludeImages
					? includeImagesValue
					: undefined,
			};
			const file = await reportMeta.run({
				report,
				studentId: selectedStudent?.id,
				classIds: selectedClasses.map((item) => item.id),
			});

			const link = document.createElement("a");
			link.href = window.URL.createObjectURL(file);

			const timestamp = dayjs().format("YYYY-MM-DD-HH_mm");
			const extension = ExportType === "excel" ? "xlsx" : "pdf";
			const prefix = reportMeta?.prefix ?? "relatorio";
			link.download = `${prefix}_${timestamp}.${extension}`;

			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(link.href);
		} catch {
			alert("Erro ao gerar o relatório");
		} finally {
			setLoading(false);
		}
	};

	const handleGenerateReport = () => {
		if (requiresPeriod && !usesLast30DaysPeriod && (!startDate || !endDate)) {
			alert("Por favor, selecione um período válido.");
			return;
		}

		if (requiresStudent && !selectedStudent) {
			alert("Selecione um aluno para gerar este relatório.");
			return;
		}

		if (requiresClasses && selectedClasses.length === 0) {
			alert("Selecione ao menos uma turma para gerar este relatório.");
			return;
		}

		if (selectedReportMeta) {
			fetchReportData(
				selectedReportMeta,
				requiresPeriod && usesLast30DaysPeriod
					? dayjs().utc().subtract(30, "day").startOf("day").toISOString()
					: startDate,
				requiresPeriod && usesLast30DaysPeriod
					? dayjs().utc().endOf("day").toISOString()
					: endDate,
				type,
				includeImages,
			);
		}
		handleCloseDialog();
	};

	const columns = [
		{ id: "reportName", label: "Relatório", width: "50%" },
		{
			id: "group",
			label: "Grupo",
			width: "25%",
			render: (_groupValue: string, _rowIndex: number, row?: ReportRow) => {
				const groups = row?.group ?? [];

				return (
					<Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
						{groups.map((group) => (
							<Chip
								key={group}
								size="small"
								label={group}
								variant="outlined"
								sx={(theme) => {
									const tone = groupColorMap[group];

									if (tone === "neutral") {
										return {
											fontWeight: 600,
											borderColor: theme.palette.grey[600],
											color: theme.palette.grey[300],
											backgroundColor: alpha(theme.palette.grey[500], 0.12),
										};
									}

									return {
										fontWeight: 600,
										borderColor: theme.palette[tone].main,
										color: theme.palette[tone].light,
										backgroundColor: alpha(theme.palette[tone].main, 0.16),
									};
								}}
							/>
						))}
					</Box>
				);
			},
		},
		{
			id: "id",
			label: "Ação",
			width: "25%",
			render: (id: string) => (
				<Button
					variant="contained"
					color="primary"
					onClick={() => handleOpenDialog(Number(id))}
					sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
				>
					Gerar
				</Button>
			),
		},
	];

    const normalizeDateToIso = (value: unknown) => {
		if (!value) return "";
		const parsed = dayjs(value as string | number | Date | Dayjs);
		// Removemos o .utc() daqui, o toISOString já faz o trabalho de jogar pro padrão Z.
		return parsed.isValid() ? parsed.toISOString() : ""; 
	};

	const normalizeStartDateToIso = (value: unknown) => {
		if (!value) return "";
		const parsed = dayjs(value as string | number | Date | Dayjs);
		return parsed.isValid() ? parsed.utc().startOf("day").toISOString() : "";
	};

	const normalizeEndDateToIso = (value: unknown) => {
		if (!value) return "";
		const parsed = dayjs(value as string | number | Date | Dayjs);
		return parsed.isValid() ? parsed.utc().endOf("day").toISOString() : "";
	};

	const isGenerateDisabled =
		loading ||
		(requiresStudent && !selectedStudent) ||
		(requiresClasses && selectedClasses.length === 0) ||
		(requiresPeriod && !usesLast30DaysPeriod && (!startDate || !endDate));

	const sortedReports = useMemo<ReportRowView[]>(
		() =>
			[...reports]
				.sort((a, b) =>
					a.reportName.localeCompare(b.reportName, "pt-BR", {
						sensitivity: "base",
					}),
				)
				.map((report, index) => ({ ...report, sortOrder: index })),
		[reports],
	);

	const paginatedReports = useMemo(
		() =>
			sortedReports.slice(
				(pagination.pageNumber - 1) * pagination.pageSize,
				pagination.pageNumber * pagination.pageSize,
			),
		[pagination, sortedReports],
	);

	return (
		<Box
			sx={{
				width: "100%",
				p: 0,
				backgroundColor: "transparent",
			}}
		>
			<TableComponent
				columns={columns}
				totalCount={sortedReports.length}
				rows={paginatedReports}
				onChangePageOrRowsPerPage={setPagination}
				sortByName={"sortOrder"}
				defaultOrder="asc"
				title="Central de Relatórios"
				currentPageNumber={pagination.pageNumber}
			/>

			<Dialog
				open={isDialogOpen}
				onClose={handleCloseDialog}
				fullWidth
				maxWidth="sm"
				slotProps={{
					paper: {
						sx: {
							borderRadius: 2,
							overflow: "hidden",
						},
					},
				}}
			>
				<DialogTitle
					component="div"
					sx={{
						pb: 1.25,
						borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
					}}
				>
					<Typography variant="h6" fontWeight={700}>
						Gerar relatório
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
						{selectedReportMeta?.reportName ??
							"Selecione os parâmetros de geração"}
					</Typography>
				</DialogTitle>
				<DialogContent sx={{ pt: 2, pb: 1.5 }}>
					<Grid container spacing={2}>
						{usesLast30DaysPeriod ? (
							<Grid item xs={12}>
								<Box
									sx={{
										px: 1.5,
										py: 1,
										borderRadius: 1.5,
										backgroundColor: (theme) =>
											alpha(theme.palette.info.main, 0.12),
										border: (theme) =>
											`1px solid ${alpha(theme.palette.info.main, 0.35)}`,
									}}
								>
									<Typography variant="body2">
										Este relatório usa período fixo dos últimos 30 dias.
									</Typography>
								</Box>
							</Grid>
						) : requiresPeriod ? (
							<>
								<Grid item xs={12} sm={6}>
									<Box sx={{ mt: 1 }}>
										<DatePickerComponent
											type="datetime"
											value={startDate ? dayjs(startDate) : null} 
											onChange={(val) => setStartDate(normalizeDateToIso(val))}
											label="Início"
											disableFuture={true}
										/>
									</Box>
								</Grid>

								<Grid item xs={12} sm={6}>
									<Box sx={{ mt: 1 }}>
										<DatePickerComponent
											type="datetime"
											value={endDate ? dayjs(endDate) : null} 
											onChange={(val) => setEndDate(normalizeDateToIso(val))}
											label="Fim"
											disableFuture={true}
										/>
									</Box>
								</Grid>
							</>
						) : (
							<Grid item xs={12}>
								<Box
									sx={{
										px: 1.5,
										py: 1,
										borderRadius: 1.5,
										backgroundColor: (theme) =>
											alpha(theme.palette.info.main, 0.12),
										border: (theme) =>
											`1px solid ${alpha(theme.palette.info.main, 0.35)}`,
									}}
								>
									<Typography variant="body2">
										Este relatório não utiliza filtro de período.
									</Typography>
								</Box>
							</Grid>
						)}

						{requiresStudent && (
							<Grid item xs={12}>
								<Autocomplete
									options={studentOptions}
									value={selectedStudent}
									onChange={(_, value) => setSelectedStudent(value)}
									inputValue={studentQuery}
									onInputChange={(_, value) => setStudentQuery(value)}
									loading={isStudentLoading}
									isOptionEqualToValue={(option, value) =>
										option.id === value.id
									}
									getOptionLabel={(option) => option.name}
									noOptionsText="Nenhum aluno encontrado"
									filterOptions={(options, state) => {
										const query = state.inputValue.trim().toLowerCase();
										if (!query) return options;

										return options.filter((student) => {
											const name = student.name?.toLowerCase() ?? "";
											const registration =
												student.registration?.toLowerCase() ?? "";
											const cpf = student.cpf?.toLowerCase() ?? "";
											return (
												name.includes(query) ||
												registration.includes(query) ||
												cpf.includes(query)
											);
										});
									}}
									renderOption={(props, option) => (
										<Box
											component="li"
											{...props}
											key={`${option.id ?? "no-id"}-${option.registration ?? "no-reg"}-${option.cpf ?? "no-cpf"}-${option.name}`}
											sx={{ py: 0.75 }}
										>
											<Box>
												<Typography variant="body2" fontWeight={600}>
													{option.name}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{getStudentIdentifier(option)}
												</Typography>
											</Box>
										</Box>
									)}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Aluno"
											placeholder="Digite nome, matrícula ou CPF"
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{isStudentLoading ? (
															<CircularProgress color="inherit" size={18} />
														) : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
							</Grid>
						)}

						{requiresClasses && (
							<Grid item xs={12}>
								<Autocomplete
									multiple
									disableCloseOnSelect
									options={classOptions}
									value={selectedClasses}
									onChange={(_, value) => setSelectedClasses(value)}
									loading={isClassLoading}
									isOptionEqualToValue={(option, value) =>
										option.id === value.id
									}
									getOptionLabel={(option) => option.name}
									renderOption={(props, option, { selected }) => (
										<Box component="li" {...props} key={option.id}>
											<Checkbox checked={selected} sx={{ mr: 1 }} />
											{option.name}
										</Box>
									)}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Turmas"
											placeholder="Selecione uma ou mais turmas"
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{isClassLoading ? (
															<CircularProgress color="inherit" size={18} />
														) : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
							</Grid>
						)}

						<Grid item xs={12}>
							<FormControl fullWidth size="small">
								<InputLabel id="report-type-label">Tipo</InputLabel>
								<Select
									labelId="report-type-label"
									value={type}
									label="Tipo"
									onChange={(e) => setType(e.target.value)}
								>
									<MenuItem value="pdf">PDF</MenuItem>
									<MenuItem value="excel">Excel</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12}>
							<Box sx={{ px: 0.5 }}>
								<FormControlLabel
									sx={{ my: 0 }}
									control={
										<Checkbox
											checked={includeImages}
											onChange={(e) => setIncludeImages(e.target.checked)}
											disabled={!selectedReportMeta?.supportsIncludeImages}
										/>
									}
									label="Incluir imagens"
								/>
								{!selectedReportMeta?.supportsIncludeImages && (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block", pl: 4.5, mt: -0.25 }}
									>
										Este relatório não suporta coluna de imagens.
									</Typography>
								)}
							</Box>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions
					sx={{
						px: 3,
						pb: 2,
						pt: 1.5,
						gap: 1,
						borderTop: (theme) => `1px solid ${theme.palette.divider}`,
					}}
				>
					<Button
						onClick={handleCloseDialog}
						color="secondary"
						variant="contained"
						sx={{ minWidth: 110 }}
					>
						Cancelar
					</Button>
					<Button
						onClick={handleGenerateReport}
						variant="contained"
						color="primary"
						sx={{ minWidth: 110 }}
						disabled={isGenerateDisabled}
					>
						Gerar
					</Button>
				</DialogActions>
			</Dialog>

			{/* LOADING */}
			<Backdrop
				open={loading}
				sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
		</Box>
	);
};

export default ReportTable;
